import { FieldValues, get, Path, useForm, UseFormReturn } from 'react-hook-form';

import { useTranslator } from '@/hooks/useTranslator';

type RegisterOptions<TFieldValues> = {
  name: Path<TFieldValues>;
  label: string;
};

type CustomRegisterReturn<TFieldValues extends FieldValues> = ReturnType<
  UseFormReturn<TFieldValues>['register']
> & {
  label: string;
  error: boolean;
  helperText?: string;
};

export type FormWithErrorsRegister<TFieldValues extends FieldValues> = (
  options: RegisterOptions<TFieldValues>,
) => CustomRegisterReturn<TFieldValues>;

export type UseFormWithErrorsReturn<TFieldValues extends FieldValues> = Omit<
  UseFormReturn<TFieldValues>,
  'register'
> & {
  register: FormWithErrorsRegister<TFieldValues>;
};

export function useFormWithErrors<TFieldValues extends FieldValues>(
  parameters: Parameters<typeof useForm<TFieldValues>>[0],
): UseFormWithErrorsReturn<TFieldValues> {
  const tr = useTranslator();
  const methods = useForm<TFieldValues>(parameters);
  const {
    register,
    formState: { errors },
  } = methods;

  const registerWithErrors = ({
    name,
    label,
  }: RegisterOptions<TFieldValues>): CustomRegisterReturn<TFieldValues> => {
    const error = get(errors, name);
    const errorMessage = error ? (error.message ? tr(error.message, label) : undefined) : undefined;

    return {
      ...register(name),
      error: Boolean(error),
      label: label,
      helperText: errorMessage,
    };
  };

  return {
    ...methods,
    register: registerWithErrors,
  };
}
